import { createFileRoute } from "@tanstack/react-router";
import {
  PaxTable,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  CardHeader,
  Card,
  PaxSearchBar,
  SortableHeaderCell,
  useTranslation,
  useSortedTable,
  UseSortedTableResult,
  PaxErrorPage,
  PaxMultiSelectDropdown,
  useSnackbar,
} from "@ncino/pax-ui";
import { alpha, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { Fragment, useMemo, useState, ChangeEvent } from "react";
import { INSTITUTION_TYPE_LABEL_KEYS, InstitutionsTableRow } from "./-internal/types";
import { useHeaders } from "./-internal/institutionsTableComponents";
import { currencyFormatter } from "@/shared/utility/stringFormatters";
import Skeleton from "@mui/material/Skeleton";
import {
  useInstitutionsPageDataQuery,
  useAddFavoriteInstitutionMutation,
  useRemoveFavoriteInstitutionMutation,
  useFavoriteInstitutionsQuery,
  FavoriteInstitutionsQuery,
  InstitutionType,
} from "@/services/graphql/reactQuery";
import { PageHeader } from "@/shared/components/PageHeader";
import { useOptimisticUpdate } from "@/services/graphql/queryUtil";
import Alert from "@mui/material/Alert";
import { PageContentContainer } from "@/shared/components/PageContentContainer";
import { FilterAlt } from "@mui/icons-material";
import { CheckboxCell } from "@/shared/components/CheckboxCell";
import StarRounded from "@mui/icons-material/StarRounded";

export const Route = createFileRoute("/institutions/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation("institutions");
  const theme = useTheme();

  const {
    data: favoriteInstitutions,
    isError: isErrorFavorites,
    refetch: refetchFavorites,
  } = useFavoriteInstitutionsQuery();
  const {
    data: pageData,
    isLoading,
    isError,
    refetch: refetchPageData,
  } = useInstitutionsPageDataQuery();

  const institutionsData = useMemo(() => {
    return (
      pageData?.getMarketplaceInstitutions.map((row): InstitutionsTableRow => {
        return {
          id: row.id!,
          name: row.name,
          type: row.institutionType,
          regulatorNumber: row.cuNumber ?? undefined,
          state: row.stateAbbrev ?? undefined,
          poolsBought: 0, // TODO: replace marketplace props with data
          poolsSold: 0,
          assetSize: row.totalAssets ?? undefined,
        };
      }) ?? []
    );
  }, [pageData]);

  // Assign pulled institutions data to a sorted table
  const sortableTable: UseSortedTableResult<InstitutionsTableRow> =
    useSortedTable<InstitutionsTableRow>(institutionsData, "name");
  function onSort(column: keyof InstitutionsTableRow) {
    sortableTable.sort(column);
    setTablePage(0);
  }

  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const favoriteIds = useMemo(() => {
    return new Set(favoriteInstitutions?.getFavoriteInstitutionIds);
  }, [favoriteInstitutions]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const {
    onMutate: onMutateFavorites,
    onError: onErrorFavoritesUpdate,
    onSettled: onSettledFavoritesUpdate,
  } = useOptimisticUpdate<FavoriteInstitutionsQuery>(useFavoriteInstitutionsQuery.getKey());

  const { mutate: addFavorite } = useAddFavoriteInstitutionMutation({
    onMutate: ({ institutionId }) =>
      onMutateFavorites((oldData) => {
        const update = oldData
          ? [...oldData.getFavoriteInstitutionIds, institutionId]
          : [institutionId];
        return { getFavoriteInstitutionIds: update };
      }),
    onError: onErrorFavoritesUpdate,
    onSettled: (removeFavorite) => {
      if (!removeFavorite) {
        enqueueSnackbar(t("institutionsPage.failedToAddFavorite"), { variant: "error" });
      }
      onSettledFavoritesUpdate();
    },
  });

  const { mutate: removeFavorite } = useRemoveFavoriteInstitutionMutation({
    onMutate: ({ institutionId }) =>
      onMutateFavorites((oldData) => {
        const update = oldData
          ? oldData.getFavoriteInstitutionIds.filter((id) => id !== institutionId)
          : [];
        return { getFavoriteInstitutionIds: update };
      }),
    onError: onErrorFavoritesUpdate,
    onSettled: (removeFavorite) => {
      if (!removeFavorite) {
        enqueueSnackbar(t("institutionsPage.failedToRemoveFavorite"), { variant: "error" });
      }
      onSettledFavoritesUpdate();
    },
  });

  function retryQueries() {
    if (isErrorFavorites) {
      refetchFavorites();
    }
    refetchPageData();
  }

  function toggleFavorite(event: ChangeEvent<HTMLInputElement>, id: number) {
    if (event.target.checked) {
      addFavorite({ institutionId: id });
    } else {
      removeFavorite({ institutionId: id });
    }
  }

  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term && !typeFilter.length && !showFavoritesOnly) return sortableTable.items;

    return sortableTable.items.filter((row) => {
      const matchesSearch =
        !term ||
        row.name.toLowerCase().includes(term) ||
        String(row.regulatorNumber).includes(term);
      const matchesType = typeFilter.length === 0 || typeFilter.includes(row.type);
      const matchesFavorites = !showFavoritesOnly || favoriteIds.has(row.id);
      return matchesSearch && matchesType && matchesFavorites;
    });
  }, [searchTerm, typeFilter, showFavoritesOnly, favoriteIds, sortableTable.items]);

  // Pagination state variables
  const [tablePage, setTablePage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(10);

  // Calc the number of empty rows on the last page
  const { emptyRows, totalPages } = useMemo(() => {
    const emptyRows = tableRowsPerPage - (filteredData.length % tableRowsPerPage);
    const totalPages = Math.ceil(filteredData.length / tableRowsPerPage);

    return { emptyRows, totalPages };
  }, [filteredData, tableRowsPerPage]);

  // Headers defined in institutionsTableComponents.tsx
  const headers = useHeaders();

  return (
    <>
      <PageHeader title={t("institutionsPage.title")} subheader={t("institutionsPage.subtitle")} />
      <PageContentContainer>
        {isError ? (
          <PaxErrorPage
            header={t("institutionsPage.errorTitle")}
            subtext={t("institutionsPage.errorSubtext")}
            actionLabel={t("institutionsPage.tryAgain")}
            onAction={retryQueries}
          />
        ) : (
          <Card variant="outlined">
            <CardHeader title={t("institutionsPage.tableName")} />
            <Box sx={{ px: 2, pb: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <PaxSearchBar
                shrinkLabel={true}
                value={searchTerm}
                onChange={(value: string) => {
                  setSearchTerm(value);
                  setTablePage(0);
                }}
                placeholder={t("institutionsPage.searchPlaceholder")}
                label={t("institutionsPage.searchLabel")}
                ariaLabel={t("institutionsPage.searchPlaceholder")}
              />
              <PaxMultiSelectDropdown
                label={t("institutionsPage.filterLabel")}
                options={Object.values(InstitutionType).map((value) => ({
                  value,
                  label: t(INSTITUTION_TYPE_LABEL_KEYS[value]),
                }))}
                selectedValues={typeFilter}
                onChange={(values) => {
                  setTypeFilter(values);
                  setTablePage(0);
                }}
                startIcon={<FilterAlt fontSize="small" />}
                showEndArrow={false}
                centerContent={true}
                buttonSx={{
                  minWidth: "110px",
                  height: "40px",
                  "& .MuiTypography-root": {
                    color: "info.main",
                    fontWeight: 500,
                    letterSpacing: ".8px",
                  },
                  borderColor: (theme) => theme.palette.divider,
                  "& .MuiSvgIcon-root": { color: "info.main" },
                  "&:hover": {
                    borderColor: "text.primary",
                  },
                }}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  setShowFavoritesOnly((prev) => !prev);
                  setTablePage(0);
                }}
                startIcon={<StarRounded />}
                sx={{
                  minWidth: "125px",
                  height: "40px",
                  textTransform: "none",
                  fontSize: "0.875rem",
                  letterSpacing: "0.4px",
                  color: showFavoritesOnly ? theme.palette.primary.main : theme.palette.info.main,
                  borderColor: showFavoritesOnly
                    ? theme.palette.primary.main
                    : theme.palette.divider,
                  backgroundColor: showFavoritesOnly
                    ? alpha(theme.palette.primary.main, 0.2)
                    : "transparent",
                }}
              >
                {t("institutionsPage.favoritesLabel")}
              </Button>
            </Box>
            {isErrorFavorites ? (
              <Alert
                severity="error"
                action={
                  <Button
                    variant="outlined"
                    onClick={() => {
                      refetchFavorites();
                    }}
                  >
                    {t("institutionsPage.tryAgain")}
                  </Button>
                }
              >
                {" "}
                {t("institutionsPage.refetchFavorites")}
              </Alert>
            ) : null}
            <PaxTable
              ariaLabel={t("institutionsPage.tableName")}
              rowHover={filteredData?.length > 0}
              fixedLayout={true}
              rowHeight={75}
              borderedTop
              isEmpty={!isLoading && filteredData.length === 0}
              emptyStateTitle={t("institutionsPage.noResultsTitle")}
              emptyStateSubtext={t("institutionsPage.noResultsSubtext")}
              emptyStateAction={
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSearchTerm("");
                    setTypeFilter([]);
                    setShowFavoritesOnly(false);
                    setTablePage(0);
                  }}
                >
                  {t("institutionsPage.viewAllInstitutions")}
                </Button>
              }
              pagination={{
                count: filteredData.length,
                page: tablePage,
                rowsPerPage: tableRowsPerPage,
                onPageChange: (newPage) => setTablePage(newPage),
                onRowsPerPageChange: (newRowsPerPage) => {
                  setTableRowsPerPage(newRowsPerPage);
                  setTablePage(0);
                },
              }}
            >
              <colgroup>
                {/* Column groups are defined this way to have one fixed with column to set a minimum width and one flexible column to expand when the table size increases */}
                {headers.map((header, index) => (
                  <Fragment key={header.text + "_columnWidth_" + index}>
                    <col width={header.minWidth} />
                    <col />
                  </Fragment>
                ))}
              </colgroup>
              <TableHead>
                <TableRow>
                  {headers.map((header, index) =>
                    header.columnKey && header.sortable ? (
                      <SortableHeaderCell
                        colSpan={2}
                        column={header.columnKey}
                        sortColumn={sortableTable.column}
                        sortDirection={sortableTable.direction}
                        onSort={onSort}
                        key={"header_" + index}
                      >
                        {header.text}
                      </SortableHeaderCell>
                    ) : (
                      <TableCell colSpan={2} key={"header_" + index}>
                        {header.text}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <>
                    {Array.from({ length: tableRowsPerPage }).map((_, i) => (
                      <TableRow key={`skeleton_${i}`}>
                        {headers.map((_, colIndex) => (
                          <TableCell colSpan={2} key={`skeleton_${i}_col_${colIndex}`}>
                            <Skeleton variant="rectangular" height={24} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </>
                ) : (
                  filteredData
                    .slice(tablePage * tableRowsPerPage, (tablePage + 1) * tableRowsPerPage)
                    .map((row, index) => (
                      <TableRow key={"institution_list_" + index}>
                        <CheckboxCell
                          title={row.name}
                          colSpan={2}
                          variant={"favorite"}
                          checked={favoriteIds.has(row.id)}
                          routeProps={{
                            to: "/institutions/$id",
                            params: { id: row.id.toString() },
                            expand: true,
                          }}
                          onChange={(e) => toggleFavorite(e, row.id)}
                          aria-label={`${row.name}_favorite_indicator`}
                        >
                          <Typography
                            variant="body2"
                            whiteSpace={"wrap"}
                            overflow={"hidden"}
                            display={"-webkit-box"}
                            sx={{ WebkitLineClamp: "2", WebkitBoxOrient: "vertical" }}
                          >
                            {row.name}
                          </Typography>
                        </CheckboxCell>
                        <TableCell colSpan={2}>
                          {t(INSTITUTION_TYPE_LABEL_KEYS[row.type])}
                        </TableCell>
                        <TableCell colSpan={2}>{row.regulatorNumber}</TableCell>
                        <TableCell colSpan={2}>{row.state}</TableCell>
                        <TableCell colSpan={2}>{row.poolsBought}</TableCell>
                        <TableCell colSpan={2}>{row.poolsSold}</TableCell>
                        <TableCell colSpan={2}>
                          {row.assetSize ? currencyFormatter.format(row.assetSize) : ""}
                        </TableCell>
                      </TableRow>
                    ))
                )}
                {/* empty rows, pages are 0 indexed, ignore if all rows fit on one page */}
                {tablePage === totalPages - 1 && tablePage > 0
                  ? Array(emptyRows)
                      .fill({})
                      .map((index) => <TableRow key={"empty_row_" + index} />)
                  : null}
              </TableBody>
            </PaxTable>
          </Card>
        )}
      </PageContentContainer>
    </>
  );
}
